<template>
  <div :class="[$style.leadQualificationBox]">
    <div :class="$style.header">
      <span :class="$style.leftbox">{{ title }}</span>
      <div :class="$style.rightbox">
        <HgBadge :text="badgeText" small />
        <HgTooltip :class="$style.tooltip" @click="trackTooltipCTA">
          <div :class="$style.tooltipTitle">{{ tooltipTitle }}</div>
          <p>{{ tooltipText }}</p>
        </HgTooltip>
      </div>
    </div>
    <div :class="$style.question">
      <HgSelect
        v-model="formState.monthlyIncome"
        name="monthlyIncome"
        :options="monthlyIncomeOptions"
        :label="label"
        @update:modelValue="handleMonthlyIncomeSelectedValue"
      />

      <HgButton
        v-if="isDisplayUserProfileCreateCTA()"
        type="button"
        :class="$style.profileCTAWrapper"
        theme="secondary-bordered"
        @click="onClickCreateProfileCTA"
      >
        {{ t('leadQualification.tenantProfile.cta.text') }}
      </HgButton>
    </div>

    <HgToast :isVisible="isProfileSuccessMessageDisplay" :class="$style.notificationToast">
      <HgNotificationMessage :title="profileSuccessMessageTitle" theme="success">
        {{ profileSuccessMessage }}
      </HgNotificationMessage>
    </HgToast>
  </div>
</template>

<script lang="ts" setup>
import {
  HgBadge,
  HgButton,
  HgNotificationMessage,
  HgSelect,
  HgToast,
  HgTooltip,
} from '@smg-real-estate/domus';
import { Listing } from '@smg-real-estate/hg-rets-schema';
import { computed, onMounted, reactive, ref } from 'vue';

import { getProfileFlagsFromUrl } from '@/components/ContactForm/helpers';
import { InteractionEvent, InteractionGoal } from '@/helpers/GTM/GTMParameters';
import { trackEventHarmonized } from '@/helpers/GTM/GTMTrack';
import { ppaListerPrefixes } from '@/helpers/ListingStats/types';
import { useI18n } from 'vue-i18n';
import { generateMonthlyIncomeSelectOptions } from './priceOptions';

const { locale, t } = useI18n();

defineOptions({
  name: 'LeadQualificationFieldsForRent',
});

const emit = defineEmits<{
  onMonthlyIncomeChanged: [value: string];
}>();

export interface Props {
  listing: Listing;
  initialMonthlyIncome: string | undefined;
  hadProfileAlready: boolean;
}

const props = withDefaults(defineProps<Props>(), {
  initialMonthlyIncome: '',
  hadProfileAlready: false,
});
const formState = reactive({
  monthlyIncome: props.initialMonthlyIncome,
});

const isExistingProfile = ref(false);
const isNewProfile = ref(false);

const title = computed(() => {
  return isDisplayUserProfileCreateCTA()
    ? t('leadQualification.tenantProfile.title')
    : t('leadQualification.householdIncome.title');
});
const label = computed(() => t('leadQualification.householdIncome.label'));
const badgeText = computed(() => t('leadQualification.householdIncome.badge'));
const tooltipTitle = computed(() => t('leadQualification.householdIncome.tooltip.title'));
const tooltipText = computed(() => t('leadQualification.householdIncome.tooltip.text'));
const monthlyIncomeOptions = computed(() => generateMonthlyIncomeSelectOptions(t, locale.value));
const isPPAListing = computed(() => {
  return ppaListerPrefixes.some((prefix) => props.listing?.lister?.id?.startsWith(prefix));
});
const isProfileSuccessMessageDisplay = computed(() => {
  return isExistingProfile.value || isNewProfile.value;
});
const profileSuccessMessageTitle = computed(() => {
  return isExistingProfile.value
    ? t('leadQualification.tenantProfile.messageExistingProfile.success.title')
    : t('leadQualification.tenantProfile.message.success.title');
});
const profileSuccessMessage = computed(() => {
  return isExistingProfile.value
    ? t('leadQualification.tenantProfile.messageExistingProfile.success.text')
    : t('leadQualification.tenantProfile.message.success.text');
});

onMounted(() => {
  // Check if the URL contains the query parameter 'existing=true' or 'newProfile=true'
  const flags = getProfileFlagsFromUrl(window.location.search);
  isExistingProfile.value = flags.isExistingProfile;
  isNewProfile.value = flags.isNewProfile;
});

function trackTooltipCTA(): void {
  trackEventHarmonized(InteractionEvent.PDP_INTERACTION, {
    h_event_category_006: InteractionEvent.PDP_INTERACTION,
    h_event_action_007: InteractionGoal.OPEN_TOOLTIP,
    h_event_label_008: 'financial_profile',
  });
}

function handleMonthlyIncomeSelectedValue(selectedValue: string) {
  emit('onMonthlyIncomeChanged', selectedValue);
  trackEventHarmonized(InteractionEvent.PDP_INTERACTION, {
    h_event_category_006: InteractionEvent.PDP_INTERACTION,
    h_event_action_007: InteractionGoal.OPEN_DROPDOWN,
    h_event_label_008: 'financial_profile',
  });
}

function isDisplayUserProfileCreateCTA(): boolean {
  if (props.hadProfileAlready) {
    return false;
  }
  return (
    !!(
      props.listing.lister?.allowToContact && props.listing.valueAddedServices?.isTenantPlusListing
    ) || isPPAListing.value
  );
}

function onClickCreateProfileCTA() {
  trackEventHarmonized(InteractionEvent.PDP_INTERACTION, {
    h_event_category_006: InteractionEvent.PDP_INTERACTION,
    h_event_action_007: InteractionGoal.CREATE_PROFILE,
  });

  window.location.href = createProfileCtaUrl();
}

function createProfileCtaUrl(): string {
  const url = new URL(t('leadQualification.tenantProfile.cta.link') as string);
  const currentUrl = window.location.href;

  url.searchParams.append('callbackUrl', currentUrl);
  url.searchParams.append('entryPoint', 'contact_form_web');
  url.searchParams.append('openModal', 'true');

  return url.toString();
}
</script>

<style lang="scss" module>
@import '@/assets/variables';

.leadQualificationBox {
  padding: $space-25;
  background-color: $color-grey-30;
  border-radius: 0.5rem;
}

.header {
  display: flex;
  flex-direction: row;
  padding: $space-25;
}

.leftbox {
  display: flex;
  overflow: hidden;

  @include heading-4;
}

.rightbox {
  display: flex;
  flex: 1;
  flex-direction: row;
  align-items: center;
  justify-content: end;
}

.tooltip {
  margin-left: 0.25rem;
}

.tooltipTitle {
  @include heading-4;
}

.question {
  display: flex;
  flex-direction: column;
  padding: $space-25;
}

.profileCTAWrapper {
  margin-top: $space-100;
}
</style>
